import { useState, useEffect, useCallback, useRef } from 'react';
import { useGetCustomerDetails } from '@agria/paws/src/hooks/entra/useGetCustomerDetails';
import { useGetAffiliateDetails } from '@agria/paws/src/hooks/entra/useGetAffiliateDetails';
import { useAffiliateLookup } from '@agria/paws/src/hooks/entra/useAffiliateLookup';
import { useGetAffiliateTypes } from '@agria/paws/src/hooks/useGetAffiliateTypes';
import { Affiliate, CustomerDetails } from '@agria/paws/src/types';
import { updateHeaders } from '@agria/paws/src/utils/updateHeaders';
import { encryptData, decryptData } from '../utils/cryptoUtils';
import {
  User,
  CustomerUser,
  AffiliateUser,
  MergedAffiliateUser,
} from '../types/types';
import { getAffiliateCategoryByTypeId } from '../utils/getAffiliateCategoryByTypeId';

interface UserDataState {
  user: User | null;
  customer: CustomerUser | null;
  affiliate: MergedAffiliateUser | null;
  isPending: boolean;
}

interface UserDataManager extends UserDataState {
  setUser: (user: User | null) => void;
  setCurrentCustomer: (data: CustomerDetails) => void;
  setCurrentAffiliate: (data: Affiliate) => void;
  refetchUserData: () => void;
}

/**
 * Utility to check if a value is meaningful (not null, undefined, or empty string)
 */
const isMeaningful = (value: any): boolean =>
  value !== null &&
  value !== undefined &&
  !(typeof value === 'string' && value.trim() === '');

/**
 * Merges two objects, prioritizing meaningful values from the second object
 * Used to combine affiliate data from different sources
 */
const mergeData = <
  T extends Record<string, any>,
  U extends Record<string, any>
>(
  obj1: T,
  obj2: U
): T & U => {
  const merged: Record<string, any> = {};
  const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);

  allKeys.forEach((key) => {
    const val1 = obj1[key];
    const val2 = obj2[key];

    let selectedValue;
    if (isMeaningful(val2)) {
      selectedValue = val2;
    } else if (isMeaningful(val1)) {
      selectedValue = val1;
    } else if (val2 !== undefined) {
      selectedValue = val2;
    } else {
      selectedValue = val1;
    }
    merged[key] = selectedValue;
  });

  return merged as T & U;
};

/**
 * Manages user data state and the complex data fetching flow
 *
 * Flow explanation:
 * 1. User object is set (from auth or session storage)
 * 2. Based on user type (customer/affiliate), fetch initial data
 * 3. For affiliates: Initial fetch doesn't include type ID, so we need a second lookup
 * 4. Once all data is available, merge and set the final user state
 */
export const useUserDataManager = (): UserDataManager => {
  const [user, setUserState] = useState<User | null>(null);
  const [customer, setCustomer] = useState<CustomerUser | null>(null);
  const [affiliate, setAffiliate] = useState<MergedAffiliateUser | null>(null);
  const [isPending, setIsPending] = useState<boolean>(false);

  // Use refs to track if we've already processed certain data to prevent loops
  const processedUserRef = useRef<string | null>(null);
  const processedCustomerRef = useRef<string | null>(null);
  const processedAffiliateRef = useRef<string | null>(null);

  // Data fetching hooks
  const { data: affiliateTypes } = useGetAffiliateTypes();
  const {
    data: customerData,
    fetchCustomerDetails,
    isError: customerError,
    isPending: customerIsLoading,
  } = useGetCustomerDetails();

  const {
    data: affiliateData,
    fetchAffiliateDetails,
    isError: affiliateError,
    isPending: affiliateLoading,
  } = useGetAffiliateDetails();

  const {
    mutate: lookupAffiliate,
    data: affiliateLookupData,
    isError: affiliateLookupError,
    isPending: affiliateLookupLoading,
  } = useAffiliateLookup(); // STEP 9: Set user and handle session storage + headers
  // This is called from AuthManager after successful login
  const setUser = useCallback((newUser: User | null) => {
    if (newUser) {
      // STEP 11A: Setting up API authentication headers

      // Update API headers if tokens are available
      if (newUser.UserAccessToken && newUser.UserIdToken) {
        updateHeaders([
          { Authorization: `bearer ${newUser.UserAccessToken}` },
          { 'X-ID-Token': newUser.UserIdToken },
        ]);
      }

      // STEP 11B: Encrypting and storing user data in session storage
      // Persist to session storage
      sessionStorage.setItem(
        'user',
        encryptData(JSON.stringify(newUser)) as string
      );
    } else {
      // Clear refs when user is cleared
      processedUserRef.current = null;
      processedCustomerRef.current = null;
      processedAffiliateRef.current = null;
    }

    // STEP 11C: User state updated - triggering data fetch chain
    setUserState(newUser);
  }, []);

  // STEP 12A: Customer data processing and state management
  const setCurrentCustomer = useCallback((data: CustomerDetails) => {
    // STEP 12A: Processing customer data and updating state

    const customerWithFullName: CustomerUser = {
      ...data,
      FullName: `${data?.FirstName} ${data?.LastName}`,
      FirstName: `${data?.FirstName}`,
    };

    setCustomer(customerWithFullName);
    // STEP 12A: Customer state updated - NavigationManager will now set customer navigation
  }, []);

  // STEP 12B: Affiliate data processing with complex merging logic
  // This is more complex because affiliate data comes from TWO different API calls
  const setCurrentAffiliate = useCallback(
    (data: Affiliate) => {
      // STEP 12B: Processing affiliate data from lookup response

      if (affiliateTypes && affiliateData) {
        // STEP 12B1: Merging affiliate data from two different sources

        const affiliateWithFullName: AffiliateUser = {
          ...data,
          FullName: `${data.Forename} ${data.Surname}`,
          FirstName: `${data.Forename}`,
          AffiliateType:
            affiliateTypes.find((type) => type.Id === data.AffiliateTypeId)
              ?.Name || 'undefined',
          AffiliateCategory: getAffiliateCategoryByTypeId(
            data.AffiliateTypeId,
            data.AppointedRepresentativeStatusId,
            affiliateTypes
          ),
        };

        // STEP 12B2: Merging initial affiliate data with lookup data
        // Merge the fetched affiliate data with the lookup data
        const mergedUser: MergedAffiliateUser = mergeData(
          affiliateData,
          affiliateWithFullName
        );

        setAffiliate(mergedUser);
        // STEP 12B: Affiliate state updated - NavigationManager will now set navigation based on affiliate category
      }
    },
    [affiliateTypes, affiliateData]
  );

  // Function to manually refetch user data
  const refetchUserData = useCallback(() => {
    // Reset processed refs to allow fresh data processing
    processedUserRef.current = null;
    processedCustomerRef.current = null;
    processedAffiliateRef.current = null;

    if (user?.CustomerId) {
      fetchCustomerDetails(user.CustomerId);
    } else if (user?.AffiliateId) {
      fetchAffiliateDetails(user.AffiliateId);
    }
  }, [user, fetchCustomerDetails, fetchAffiliateDetails]);

  // Update loading state based on any pending operations
  useEffect(() => {
    if (customerIsLoading || affiliateLoading || affiliateLookupLoading) {
      setIsPending(true);
    } else {
      setIsPending(false);
    }
  }, [customerIsLoading, affiliateLoading, affiliateLookupLoading]);

  // Load user from session storage on mount
  useEffect(() => {
    const storedUser = sessionStorage.getItem('user');
    const storedUserDecrypted: User | null = storedUser
      ? (decryptData(storedUser) as User)
      : null;

    if (storedUserDecrypted) {
      // Set up authentication headers for API calls after reload
      if (
        storedUserDecrypted.UserAccessToken &&
        storedUserDecrypted.UserIdToken
      ) {
        updateHeaders([
          { Authorization: `bearer ${storedUserDecrypted.UserAccessToken}` },
          { 'X-ID-Token': storedUserDecrypted.UserIdToken },
        ]);
      }

      setUserState(storedUserDecrypted);

      // Fetch data based on user type
      if (storedUserDecrypted.CustomerId) {
        fetchCustomerDetails(storedUserDecrypted.CustomerId);
      } else if (storedUserDecrypted.AffiliateId) {
        fetchAffiliateDetails(storedUserDecrypted.AffiliateId);
      }
    }
    // This should only run once on mount, so empty deps array is correct
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // STEP 11D: Handle user changes and trigger appropriate data fetches
  // This effect is triggered when setUser() is called from AuthManager
  useEffect(() => {
    if (user) {
      const userKey = `${user.CustomerId || user.AffiliateId}`;

      // Prevent infinite loops by checking if we've already processed this user
      if (processedUserRef.current === userKey) {
        return;
      }

      // STEP 11D: User state changed, determining data fetch strategy

      // CUSTOMER FLOW: Simple single API call
      if (customerData && user.CustomerId === customerData.Id) {
        // STEP 11D1: Customer data already available, setting directly
        setCurrentCustomer(customerData);
        processedUserRef.current = userKey;
      } else if (user.CustomerId) {
        // STEP 11D2: Fetching customer details from API
        fetchCustomerDetails(user.CustomerId);
        processedUserRef.current = userKey;
      }

      // AFFILIATE FLOW: Complex two-step process due to API limitations
      else if (
        affiliateLookupData &&
        user.AffiliateId === affiliateLookupData?.Id
      ) {
        // STEP 11D3: Affiliate lookup data already available, setting directly
        setCurrentAffiliate(affiliateLookupData);
        processedUserRef.current = userKey;
      } else if (user.AffiliateId) {
        // STEP 11D4: Starting affiliate data fetch (STEP 1 of 2)
        fetchAffiliateDetails(user.AffiliateId);
        processedUserRef.current = userKey;
      }
    } else {
      // Reset when user is cleared
      processedUserRef.current = null;
    }
  }, [
    user,
    customerData,
    affiliateLookupData,
    fetchCustomerDetails,
    fetchAffiliateDetails,
    setCurrentCustomer,
    setCurrentAffiliate,
  ]);

  // STEP 11E: Affiliate Flow Part 1 - Initial fetch triggers lookup
  // This is WHY affiliate flow is complex - the API design requires 2 calls
  useEffect(() => {
    if (affiliateData && !affiliateError) {
      // STEP 11E: Initial affiliate data received, but missing type ID
      // STEP 11E: Starting affiliate lookup (STEP 2 of 2) to get complete data

      // The initial affiliateData doesn't include affiliate type ID
      // So we need to do another lookup to get complete information
      lookupAffiliate({
        agriaId: affiliateData.AgriaId,
        affiliateId: affiliateData.Id,
      });
    }
  }, [affiliateData, affiliateError, lookupAffiliate]);

  // STEP 11F: Affiliate Flow Part 2 - Lookup completes, set final affiliate state
  useEffect(() => {
    if (affiliateLookupData && !affiliateLookupError) {
      const affiliateKey = affiliateLookupData.Id;

      // Prevent infinite loops by checking if we've already processed this affiliate
      if (processedAffiliateRef.current === affiliateKey) {
        return;
      }

      // STEP 11F: Affiliate lookup complete, setting final affiliate state
      setCurrentAffiliate(affiliateLookupData);
      processedAffiliateRef.current = affiliateKey;
    }
  }, [affiliateLookupData, affiliateLookupError, setCurrentAffiliate]);

  // STEP 11G: Customer data processing when it arrives
  useEffect(() => {
    if (customerData && !customerError) {
      const customerKey = customerData.Id;

      // Prevent infinite loops by checking if we've already processed this customer
      if (processedCustomerRef.current === customerKey) {
        return;
      }

      // STEP 11G: Customer data received, setting customer state
      setCurrentCustomer(customerData);
      processedCustomerRef.current = customerKey;
    }
  }, [customerData, customerError, setCurrentCustomer]);

  return {
    user,
    customer,
    affiliate,
    isPending,
    setUser,
    setCurrentCustomer,
    setCurrentAffiliate,
    refetchUserData,
  };
};
